import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Heart, MessageCircle, Trash2, ImagePlus, X, BadgeCheck, Send, Video } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { media, KIND_LABEL, fileToDataUrl } from "../../lib/community";
import { uploadMedia } from "../../lib/social";
import { haptic } from "../../lib/haptics";

const ago = (iso) => {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

export function Avatar({ p, size = "w-10 h-10" }) {
  return p?.avatar_url
    ? <img src={media(p.avatar_url)} alt="" className={`${size} rounded-full object-cover bg-[var(--bg-warm)] shrink-0`} />
    : <span className={`${size} rounded-full bg-[var(--ink)] text-[var(--gold)] font-serif flex items-center justify-center shrink-0`}>{(p?.display_name || "?")[0].toUpperCase()}</span>;
}

export function KindBadge({ kind }) {
  return <span className="text-[9px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-[#C8A96B]/15 text-[#8a6d35]">{KIND_LABEL[kind] || kind}</span>;
}

export function Byline({ p, time }) {
  return (
    <Link to={`/community/u/${p.handle}`} className="flex items-center gap-3 min-w-0 group" data-testid={`byline-${p.handle}`}>
      <Avatar p={p} />
      <div className="min-w-0">
        <p className="font-medium text-sm flex items-center gap-1 truncate group-hover:text-[var(--gold)] transition-colors">{p.display_name}{p.verified && <BadgeCheck className="w-3.5 h-3.5 text-[var(--gold)]" />}</p>
        <p className="text-[11px] text-[var(--muted)] flex items-center gap-1.5"><KindBadge kind={p.kind} />{p.city && <span>{p.city}</span>}{time && <span>· {ago(time)}</span>}</p>
      </div>
    </Link>
  );
}

function Comments({ post, user, onCount }) {
  const [rows, setRows] = useState(null);
  const [text, setText] = useState("");
  React.useEffect(() => { api.get(`/community/posts/${post.post_id}/comments`).then((r) => setRows(r.data)); }, [post.post_id]);
  async function remove(id) {
    try { await api.delete(`/community/comments/${id}`); setRows((r) => r.filter((x) => x.comment_id !== id)); onCount(-1); }
    catch (err) { toast.error(formatApiError(err)); }
  }
  async function add(e) {
    e.preventDefault();
    try {
      const { data } = await api.post(`/community/posts/${post.post_id}/comments`, { text });
      setRows((r) => [...(r || []), data]); setText(""); onCount(1);
    } catch (err) { toast.error(formatApiError(err)); }
  }
  return (
    <div className="border-t border-[var(--line)] pt-3 mt-3 space-y-3" data-testid={`comments-${post.post_id}`}>
      {(rows || []).map((c) => (
        <div key={c.comment_id} className="flex gap-2 text-sm group"><Avatar p={c.author} size="w-7 h-7" /><p className="bg-[var(--bg)] rounded-2xl px-3 py-2 min-w-0 break-words"><span className="font-medium mr-1.5">{c.author?.display_name}</span>{c.text}</p>
          {user && (user.user_id === c.author_id || user.role === "admin") && <button onClick={() => remove(c.comment_id)} data-testid={`comment-delete-${c.comment_id}`} className="self-center p-1 text-[var(--muted)] hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>}
        </div>
      ))}
      {user && (
        <form onSubmit={add} className="flex gap-2">
          <input value={text} onChange={(e) => setText(e.target.value)} required placeholder="Add a comment…" data-testid={`comment-input-${post.post_id}`} className="flex-1 min-w-0 bg-[var(--bg)] rounded-full px-4 py-2 text-sm outline-none focus:ring-1 focus:ring-[var(--gold)]" />
          <button className="w-9 h-9 rounded-full bg-[var(--gold)] text-white flex items-center justify-center" data-testid={`comment-send-${post.post_id}`}><Send className="w-4 h-4" /></button>
        </form>
      )}
    </div>
  );
}

export function PostCard({ post, user, onDeleted }) {
  const nav = useNavigate();
  const [p, setP] = useState(post);
  const [open, setOpen] = useState(false);
  const [burst, setBurst] = useState(false);
  const canDelete = user && (user.user_id === p.author_id || user.role === "admin");

  async function like() {
    if (!user) { nav("/login?next=/community"); return; }
    haptic(10);
    if (!p.liked_by_me) { setBurst(true); setTimeout(() => setBurst(false), 700); }
    setP({ ...p, liked_by_me: !p.liked_by_me, like_count: p.like_count + (p.liked_by_me ? -1 : 1) });
    try { const { data } = await api.post(`/community/posts/${p.post_id}/like`); setP((x) => ({ ...x, liked_by_me: data.liked, like_count: data.like_count })); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  async function del() {
    if (!window.confirm("Delete this post?")) return;
    try { await api.delete(`/community/posts/${p.post_id}`); onDeleted?.(p.post_id); } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <motion.article layout initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} data-testid={`post-${p.post_id}`} className="bg-white border border-[var(--line)] rounded-3xl p-4 md:p-5">
      <div className="flex items-start justify-between gap-3">
        <Byline p={p.author} time={p.created_at} />
        {canDelete && <button onClick={del} data-testid={`post-delete-${p.post_id}`} className="p-2 rounded-full text-[var(--muted)] hover:text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>}
      </div>
      {p.text && <p className="mt-3 text-[15px] leading-relaxed whitespace-pre-wrap">{p.text}</p>}
      {p.images?.length > 0 && (
        <div className={`mt-3 grid gap-1.5 rounded-2xl overflow-hidden relative ${p.images.length > 1 ? "grid-cols-2" : ""}`} onDoubleClick={() => !p.liked_by_me && like()}>
          {p.images.map((src) => <img key={src} src={media(src)} alt="" loading="lazy" className={`w-full object-cover ${p.images.length > 1 ? "aspect-square" : "max-h-[520px]"}`} />)}
          <AnimatePresence>{burst && <motion.span initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1.2, opacity: 1 }} exit={{ scale: 1.6, opacity: 0 }} className="absolute inset-0 flex items-center justify-center pointer-events-none"><Heart className="w-24 h-24 fill-white text-white drop-shadow-xl" /></motion.span>}</AnimatePresence>
        </div>
      )}
      {p.video && <video src={media(p.video)} controls playsInline preload="metadata" className="mt-3 w-full max-h-[560px] rounded-2xl bg-black" data-testid={`post-video-${p.post_id}`} />}
      <div className="flex items-center gap-5 mt-3 text-sm text-[var(--muted)]">
        <motion.button whileTap={{ scale: 0.8 }} onClick={like} data-testid={`post-like-${p.post_id}`} className={`flex items-center gap-1.5 ${p.liked_by_me ? "text-rose-500" : "hover:text-rose-500"}`}>
          <Heart className={`w-5 h-5 ${p.liked_by_me ? "fill-current" : ""}`} /> <span data-testid={`post-like-count-${p.post_id}`}>{p.like_count}</span>
        </motion.button>
        <button onClick={() => setOpen((o) => !o)} data-testid={`post-comments-toggle-${p.post_id}`} className="flex items-center gap-1.5 hover:text-[var(--ink)]"><MessageCircle className="w-5 h-5" /> {p.comment_count}</button>
      </div>
      {open && <Comments post={p} user={user} onCount={(d) => setP((x) => ({ ...x, comment_count: x.comment_count + d }))} />}
    </motion.article>
  );
}

export function Composer({ me, onPosted, placeholder = "Share a real wedding, a tip, or a win…", wallOf = "" }) {
  const [text, setText] = useState("");
  const [imgs, setImgs] = useState([]);
  const [video, setVideo] = useState("");
  const [pct, setPct] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const vidRef = useRef(null);

  async function pickVideo(e) {
    const f = e.target.files[0];
    e.target.value = "";
    if (!f) return;
    setPct(0);
    try { setVideo(await uploadMedia(f, setPct)); } catch (err) { toast.error(err.message || formatApiError(err)); } finally { setPct(null); }
  }

  async function pick(e) {
    try {
      const files = [...e.target.files].slice(0, 4 - imgs.length);
      const urls = await Promise.all(files.map((f) => fileToDataUrl(f)));
      setImgs((x) => [...x, ...urls]);
    } catch (err) { toast.error(err.message); }
    e.target.value = "";
  }
  async function post() {
    setBusy(true);
    try {
      const { data } = await api.post("/community/posts", { text, images: imgs, video, wall_of: wallOf });
      setText(""); setImgs([]); setVideo(""); onPosted(data); haptic(12);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }
  return (
    <div className="bg-white border border-[var(--line)] rounded-3xl p-4" data-testid="composer">
      <div className="flex gap-3">
        <Avatar p={me} />
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} maxLength={2000} placeholder={placeholder} data-testid="composer-text" className="flex-1 resize-none outline-none text-[15px] pt-2 bg-transparent" />
      </div>
      {pct !== null && <div className="mt-3" data-testid="composer-video-progress"><div className="h-1.5 rounded-full bg-[var(--bg-warm)] overflow-hidden"><div className="h-full bg-[var(--gold)] transition-[width]" style={{ width: `${pct}%` }} /></div><p className="text-[11px] text-[var(--muted)] mt-1">Uploading video… {pct}%</p></div>}
      {video && <div className="relative mt-3"><video src={media(video)} className="w-full max-h-60 rounded-xl bg-black" controls data-testid="composer-video-preview" /><button onClick={() => setVideo("")} aria-label="Remove video" className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black text-white flex items-center justify-center" data-testid="composer-video-remove"><X className="w-3 h-3" /></button></div>}
      {imgs.length > 0 && (
        <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar">
          {imgs.map((src, i) => (
            <div key={i} className="relative shrink-0"><img src={src} alt="" className="w-20 h-20 rounded-xl object-cover" />
              <button onClick={() => setImgs(imgs.filter((_, k) => k !== i))} className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-black text-white flex items-center justify-center"><X className="w-3 h-3" /></button></div>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-[var(--line)]">
        <div className="flex items-center gap-4">
          <button onClick={() => fileRef.current?.click()} disabled={imgs.length >= 4} data-testid="composer-add-photo" className="flex items-center gap-1.5 text-sm text-[var(--gold)] disabled:opacity-40"><ImagePlus className="w-5 h-5" /> Photo</button>
          <button onClick={() => vidRef.current?.click()} disabled={!!video || pct !== null} data-testid="composer-add-video" className="flex items-center gap-1.5 text-sm text-[var(--gold)] disabled:opacity-40"><Video className="w-5 h-5" /> Video</button>
        </div>
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={pick} data-testid="composer-file" />
        <input ref={vidRef} type="file" accept="video/mp4,video/quicktime,video/webm" className="hidden" onChange={pickVideo} data-testid="composer-video-file" />
        <button onClick={post} disabled={busy || pct !== null || (!text.trim() && !imgs.length && !video)} data-testid="composer-submit" className="btn-gold !py-2 !px-5 disabled:opacity-40">{busy ? "Posting…" : "Post"}</button>
      </div>
    </div>
  );
}
